// Eyebrow renderer — darkens the brow region via multiply blend at low
// opacity to preserve natural hair texture. No colour selection needed —
// a universal warm-brown tint works across all skin tones.
//
// Landmarks: MediaPipe Face Mesh brow arches (upper) and the soft rim
// just below the brow (lower), traced as closed polygons.

import type { LipDetection } from '../tracking/faceLandmarker';
import { stackBlur } from './stackBlur';

const RIGHT_BROW_UPPER = [70, 63, 105, 66, 107];
const RIGHT_BROW_LOWER = [46, 53, 52, 65, 55];
const LEFT_BROW_UPPER  = [336, 296, 334, 293, 300];
const LEFT_BROW_LOWER  = [276, 283, 282, 295, 285];

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16)
  ];
}

function traceBrowPolygon(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  upper: readonly number[],
  lower: readonly number[]
): void {
  const { landmarks, imageWidth: W, imageHeight: H } = detection;
  let started = false;

  // Upper contour: brow arch, left-to-right
  for (const idx of upper) {
    const p = landmarks[idx];
    if (!p) continue;
    const x = p.x * W;
    const y = p.y * H;
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  // Lower contour: under-brow, right-to-left (closes the polygon)
  for (let i = lower.length - 1; i >= 0; i--) {
    const p = landmarks[lower[i]!];
    if (!p) continue;
    ctx.lineTo(p.x * W, p.y * H);
  }
  ctx.closePath();
}

export interface EyebrowOptions {
  hex?: string;
  opacity?: number;
}

export function renderEyebrowOverlay(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection,
  options: EyebrowOptions = {}
): { canvas: HTMLCanvasElement } {
  const W = detection.imageWidth;
  const H = detection.imageHeight;
  const hex = options.hex ?? '#5C3A2E';
  const opacity = options.opacity ?? 0.12;
  const feather = Math.max(3, Math.min(W, H) * 0.012);

  const out = document.createElement('canvas');
  out.width = W;
  out.height = H;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');
  ctx.drawImage(source, 0, 0, W, H);

  const [r, g, b] = hexToRgb(hex);

  const mask = document.createElement('canvas');
  mask.width = W;
  mask.height = H;
  const mctx = mask.getContext('2d', { willReadFrequently: true });
  if (!mctx) return { canvas: out };

  mctx.fillStyle = '#ffffff';
  mctx.beginPath();
  traceBrowPolygon(mctx, detection, RIGHT_BROW_UPPER, RIGHT_BROW_LOWER);
  traceBrowPolygon(mctx, detection, LEFT_BROW_UPPER, LEFT_BROW_LOWER);
  mctx.fill();
  stackBlur(mctx, 0, 0, W, H, feather);

  const colour = document.createElement('canvas');
  colour.width = W;
  colour.height = H;
  const cctx = colour.getContext('2d');
  if (!cctx) return { canvas: out };

  cctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
  cctx.fillRect(0, 0, W, H);
  cctx.globalCompositeOperation = 'destination-in';
  cctx.drawImage(mask, 0, 0);
  cctx.globalCompositeOperation = 'source-over';

  // Multiply preserves natural brow hair texture while darkening.
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(colour, 0, 0);
  ctx.globalCompositeOperation = 'source-over';

  return { canvas: out };
}
